import type { StrategyNode } from '../types/BaseNode';
import type { DataTypeSpec } from '../hooks/useDataTypes';
import type { FormatterTypeSpec } from '../hooks/useFormatterTypes';

export interface TimeProfile {
  kind?: string;
  resolution?: string;
  preferred_combine_on?: string;
  label?: string;
  matches_any?: boolean;
}

export function calculateNodeTimeProfile(
  node: StrategyNode | undefined,
  getDataTypeById: (id: string) => DataTypeSpec | undefined,
  getFormatterTypeById: (id: string) => FormatterTypeSpec | undefined
): TimeProfile | undefined {
  if (!node) return undefined;

  switch (node.type) {
    case 'Single':
      return calculateSingleNodeTimeProfile(node, getDataTypeById);
    case 'Formatter':
      return calculateFormatterNodeTimeProfile(node, getDataTypeById, getFormatterTypeById);
    case 'Composite':
      return calculateNodeTimeProfile((node as any).config?.left, getDataTypeById, getFormatterTypeById);
    default:
      return undefined;
  }
}

function calculateSingleNodeTimeProfile(
  node: StrategyNode,
  getDataTypeById: (id: string) => DataTypeSpec | undefined
): TimeProfile | undefined {
  const config = (node as any).config ?? {};
  const typeId = config.typeId ?? config.type_id;
  const props = config.props ?? config;
  const dataType = typeId ? getDataTypeById(typeId) : undefined;
  const selectedHour = normalizeHourValue(props?.hour);

  const profile: TimeProfile = { ...(dataType?.time_profile ?? {}) };
  const explicitType = typeof props?.time_type === 'string' ? props.time_type : undefined;
  const interval = typeof props?.interval === 'string' ? props.interval : undefined;
  const label = (explicitType ?? interval ?? profile.label)?.toLowerCase();

  if (label) {
    profile.label = label;
    profile.resolution = label;
    if (!profile.kind) {
      profile.kind = inferKindFromLabel(label);
    }
    if (!profile.preferred_combine_on) {
      profile.preferred_combine_on = inferPreferredCombineOn(profile.kind);
    }
  }

  if (!profile.kind && profile.label) {
    profile.kind = inferKindFromLabel(profile.label);
  }
  if (!profile.preferred_combine_on && profile.kind) {
    profile.preferred_combine_on = inferPreferredCombineOn(profile.kind);
  }

  if (typeId === 'constant_value') {
    profile.matches_any = true;
    profile.preferred_combine_on = 'date';
    profile.kind = profile.kind ?? 'date';
    profile.label = profile.label ?? 'constant';
    profile.resolution = profile.resolution ?? 'constant';
  }

  // A specific hour selection behaves like a daily aligned slice, so it should
  // suggest `date` rather than `exact_match`. "25" is the repo's all-hours marker.
  if (selectedHour && selectedHour !== '25') {
    profile.preferred_combine_on = 'date';
    profile.kind = 'date';
    profile.label = profile.label ?? 'hour';
    profile.resolution = profile.resolution ?? 'hour';
  }

  return profile;
}

function calculateFormatterNodeTimeProfile(
  node: StrategyNode,
  getDataTypeById: (id: string) => DataTypeSpec | undefined,
  getFormatterTypeById: (id: string) => FormatterTypeSpec | undefined
): TimeProfile | undefined {
  const config = (node as any).config ?? {};
  const formatterId = config.typeId;
  const childProfile = calculateNodeTimeProfile(config.child, getDataTypeById, getFormatterTypeById);
  const formatterType = formatterId ? getFormatterTypeById(formatterId) : undefined;
  const baseProfile = childProfile ?? {};
  const formatterProfile = formatterType?.time_profile ?? {};

  if (formatterId === 'group_by') {
    const groupBy = String(config.props?.group_by ?? 'hour').toLowerCase();
    return {
      kind: inferKindFromLabel(groupBy),
      resolution: groupBy,
      preferred_combine_on: inferPreferredCombineOn(inferKindFromLabel(groupBy)),
      label: groupBy,
    };
  }

  return {
    ...baseProfile,
    ...formatterProfile,
  };
}

function inferKindFromLabel(label?: string): string {
  const normalized = (label ?? 'timestamp').toLowerCase();
  if (normalized === 'gasday') return 'gasday';
  if (normalized === 'date' || normalized === 'day') return 'date';
  return 'timestamp';
}

function inferPreferredCombineOn(kind?: string): string {
  if (kind === 'gasday') return 'gasday';
  if (kind === 'date') return 'date';
  return 'exact_match';
}

function normalizeHourValue(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  return undefined;
}
